import React, { useEffect, useState } from "react";
import { ContentWrapper } from "@components/index";
import { PDFViewer } from "@components/PDFViewer/PDFViewer";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Space, Tabs, Typography, Dropdown, MenuProps, Modal, Form, Select, Input, Row, Col, Spin } from "antd";
import styles from "../../Contract.module.scss";
import { Icon } from "@iconify/react";
import { useQuery } from "@hooks/useQuery";
import { useNotification } from "@hooks/useNotification";
import { OverView } from "./OverView/OverView";
import { useHasAccess } from "@hooks/useHasAccess";
import { useMaskId } from "~/Hooks/useMaskId";
import { getBackUrl } from "@utils/getLocalStorage";
import pdfMake from "pdfmake/build/pdfmake";
import { Comments } from "@modules/ContractManagement/SPA/Drilldown/Comments/Comments";
import { StatusInformation } from "./StatusInformation/StatusInformation";
import { api, get } from "~/Services";
import { DocumentVerisoning } from "@modules/ContractManagement/SPA/Drilldown/DocumentVersioning/DocumentVerisoning";
import { UserType } from "~/Utils";
import { useAuth } from "~/Hooks/useAuth";
import { LoaderModal } from "@components/LoaderModal/LoaderModal";
import { printPdf } from "~/Utils/pdfmake/generate-pdf";
import { Logs } from "./Logs/Components/Logs";
import { Field, FieldProps, Formik } from "formik";
import TextArea from "antd/lib/input/TextArea";
import * as yup from "yup";
import { get as _get } from "lodash";
import { send } from "process";
import { hasKeyString, scrollToElement } from "~/Utils/CommonUtils";

export const SPADrilldown = () => {
  const { id } = useParams();
  const { unmask } = useMaskId();
  const query = useQuery();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isReviewersLoading, setIsReviewersLoading] = useState(false);
  const [isContractLoading, setIsContractLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const { openToast } = useNotification();
  const [data, setData] = useState<{ [key in string]: any }>({});
  const [reviewers, setReviewers] = useState();
  const [watchers, setWatchers] = useState();
  const [notification, setNotification] = useState();
  const [documents, setDocuments] = useState<any>([]);
  const [fullEnvelopeDetails, setFullEnvelopeDetails] = useState<any>();
  const [refreshData, setRefreshData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPDFLoading, setIsPDFLoading] = useState(false);
  const [sendModal, setSendModal] = useState(false);
  const [base64Selected, setBase64Selected] = useState("");
  const { hasPermissions, hasRole } = useHasAccess();
  const { user } = useAuth();
  const [trigger, setTrigger] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const initialValue = {
    message: "",
    subject: "",
    email: [],
    cc: [],
    bcc: [],
  };

  const validationSchema = yup.object().shape({
    message: yup.string().required("Message is required field"),
    subject: yup.string().required("Subject is required field"),
    email: yup
      .array()
      .of(yup.string().email("Invalid email format"))
      .min(1, "Email is required")
      .required("Email is required"),
    cc: yup.array().of(yup.string().email("Invalid email format")),
    bcc: yup.array().of(yup.string().email("Invalid email format")),
  });

  const activeTab = query.get("activeTab") ? query.get("activeTab") : "";

  useEffect(() => {
    if (!query.get("activeTab")) {
      navigate(`${window.location.pathname}?activeTab=contractInformation&enable=resend`, {
        replace: true,
      });
    }
    if (hasKeyString("watcher")) {
      navigate(
        `${window.location.pathname}?activeTab=${activeTab ? activeTab : "contractInformation"}&enable=${
          activeTab === "contractInformation" ? "return" : ""
        }`,
        {
          replace: true,
        }
      );
    }
    if (hasKeyString("review")) {
      navigate(`${window.location.pathname}?activeTab=${activeTab ? activeTab : "contractInformation"}`, {
        replace: true,
      });
    }
  }, []);
  const pdfKey = `${data?.projectId}`;
  const pdfName = `SPA-${data?.projectId}`;
  const submitForm = async (values: any, actions: any) => {
    setLoading(true);
    const formdata = JSON.stringify({
      mailSubject: values.subject,
      baseString: base64Selected,
      mails: values.email,
      mailMessage: values.message,
      mailsBCC: values.bcc,
      mailsCC: values.cc,
      senderEmail: user?.email,
      pdfKey: pdfKey,
      pdfName: pdfName,
    });
    api
      .post("v1/spa/mail-attachment", formdata, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        return new Promise((resolve) => setTimeout(resolve, 2000));
      })
      .then(() => {
        actions.resetForm();
        setSendModal(false);
        openToast({
          content: "Document Delivered Successfully.",
          type: "success",
        });
      })
      .catch((error) => {
        console.error("Form submission error:", error);
        openToast({
          content: "Failed to send the document. Please try again later.",
          type: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (id) {
      getEnvelopeDetails();
      getFullDetails();
      getReviewers();
      getWatchers();
      // getNotification();
    }
  }, [id, trigger, refreshData]);

  const getFullDetails = () => {
    setIsContractLoading(true);
    get(`v1/spa/get/documents/current-version?envelopeId=${id}`)
      .then((res: any) => {
        setIsContractLoading(false);
        if (res.response.data) {
          setDocuments(res.response.data);
          setFullEnvelopeDetails(res.response.data);
        }
      })
      .catch((err) => {
        setIsContractLoading(false);
      });
  };

  const getReviewers = () => {
    setIsReviewersLoading(true);
    get(`v1/workflow/get/reviewers/list?envelopeId=${id}`)
      .then((res: any) => {
        setIsReviewersLoading(false);
        if (res.response.data) {
          setReviewers(res.response.data);
        }
      })
      .catch((err) => {
        openToast({
          content: "Getting Reviewers Details failed",
          type: "error",
        });
        setIsReviewersLoading(false);
      });
  };

  const getWatchers = () => {
    setIsReviewersLoading(true);
    get(`v1/workflow/get/watchers/list?envelopeId=${id}`)
      .then((res: any) => {
        setIsReviewersLoading(false);
        if (res.response.data) {
          setWatchers(res.response.data);
        }
      })
      .catch((err) => {
        openToast({
          content: "Getting Wacthers Details failed",
          type: "error",
        });
        setIsReviewersLoading(false);
      });
  };

  const getEnvelopeDetails = () => {
    setIsLoading(true);
    get(`v1/spa/detail?envelopeId=${id}`)
      .then((res: any) => {
        if (res.response.data) {
          setData(res.response.data);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.log("log", err);
        openToast({
          content: "Getting SPA Details failed",
          type: "error",
        });
        setIsLoading(false);
      });
  };
  const getPdfData = async () => {
    let logs = await get(`v1/auditlog/envelope/get-all-logs?envelopeId=${id}`);
    let docVersion = await get(`v1/spa/get/document/versions?envelopeId=${id}&order=desc&orderBy=updatedOn`);
    let comments = await get(`v1/workflow/getCommentsList?envelopeId=${id}&order=desc&orderBy=commentedOn`);
    return {
      ...data,
      reviewers,
      watchers,
      documents,
      logs: _get(logs, "response.data.records", []),
      docVersion: _get(docVersion, "response.data", []),
      comments: _get(comments, "response.data.records", []),
    };
  };

  const handleTrigger = (value: any) => {
    setTrigger(value);
  };

  const generatePdf = async (type: string) => {
    const latestDoc = data?.documentVersionResponses?.reduce((prev: any, curr: any) =>
      Number(curr.documentId) > Number(prev.documentId) ? curr : prev
    );
    const documentName = latestDoc?.documentName;
    const fileUrl = latestDoc?.blobUrl;
    const rawDocKey = localStorage.getItem("docVersionKey");
    const docKey = rawDocKey ? JSON.parse(rawDocKey) : "";

    const isNewSpa = documentName && /^SPA\d*\(V\d+\)\.pdf$/i.test(documentName);

    setIsPDFLoading(true);
    let pdfData = await getPdfData();
    let docDefinition = printPdf(pdfData, type);
    const securePassword = `${data.projectId}`;

    if (type === "view") {
      pdfMake.createPdf(docDefinition).getBlob((blob: Blob) => {
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setIsPreviewOpen(true);
        setIsPDFLoading(false);
      });
    } else if (type === "print") {
      pdfMake.createPdf(docDefinition).print();
      setIsPDFLoading(false);
    } else if (type === "send") {
      setSendModal(true);
      pdfMake.createPdf(docDefinition).getBase64((res: any) => {
        setBase64Selected(res);
      });
      setIsPDFLoading(false);
    } else if (type === "download") {
      if (isNewSpa) {
        let base64String: string;
        try {
          base64String = await new Promise<string>((resolve, reject) => {
            pdfMake.createPdf(docDefinition).getBase64((result: string) => {
              if (result) resolve(result);
              else reject(new Error("Failed to generate base64"));
            });
          });
        } catch (error) {
          console.error("Error generating PDF base64:", error);
          setIsPDFLoading(false);
          return;
        }

        setIsPDFLoading(false);

        Modal.info({
          title: "Password Protected File",
          content: "The attachment is password protected. Please enter the Project ID No. to open it. Example:000123.",
          okText: "Download",
          closable: true,
          async onOk() {
            try {
              const payload = {
                baseString: base64String,
                password: securePassword,
                documentName: `SPA-${data.projectId}.pdf`,
              };

              const response = await api.post("v1/spa/download-encrypted-base64", payload, { responseType: "blob" });

              const blob = new Blob([response.data], { type: "application/pdf" });
              const fileURL = URL.createObjectURL(blob);

              const link = document.createElement("a");
              link.href = fileURL;
              link.download = `SPA-${data.projectId}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              URL.revokeObjectURL(fileURL);
            } catch (error) {
              console.error("Error downloading encrypted PDF:", error);
            }
          },
        });
      } else {
        setIsPDFLoading(false);

        Modal.info({
          title: "Password Protected File",
          content: "The document is password protected. Please enter the Project ID No. to open it. Example:000123.",
          okText: "Download",
          closable: true,
          async onOk() {
            try {
              const payload = {
                blobUrl: `${fileUrl}${docKey}`,
                password: securePassword,
                documentName: documentName,
              };

              const response = await api.post("v1/spa/download-encrypted-url", payload, { responseType: "blob" });

              const blob = new Blob([response.data], { type: "application/pdf" });
              const fileURL = URL.createObjectURL(blob);

              const link = document.createElement("a");
              link.href = fileURL;
              link.download = `SPA-${data.projectId}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              URL.revokeObjectURL(fileURL);
            } catch (error) {
              console.error("Error downloading PDF:", error);
            }
          },
        });
      }
    }
  };

  const dropdownItems: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <a
          rel="noopener noreferrer"
          href="javascript:;"
          onClick={() => generatePdf("download")}
        >
          Download
        </a>
      ),
    },
    {
      key: "2",
      label: (
        <a
          rel="noopener noreferrer"
          href="javascript:;"
          onClick={() => generatePdf("print")}
        >
          Print
        </a>
      ),
    },

    {
      key: "3",
      label: (
        <a
          rel="noopener noreferrer"
          href="javascript:;"
          onClick={() => generatePdf("view")}
        >
          View
        </a>
      ),
    },
    {
      key: "4",
      label: (
        <a
          rel="noopener noreferrer"
          href="javascript:;"
          onClick={() => generatePdf("send")}
        >
          Send
        </a>
      ),
    },
  ];

  const items = [
    {
      label: "Project Information",
      key: "contractInformation",
      children: (
        <OverView
          isMobile={isMobile}
          reTrigger={handleTrigger}
          data={data}
          documents={documents}
          reviewers={reviewers}
          watchers={watchers}
          fullEnvelopeDetails={fullEnvelopeDetails}
          notification={notification}
          id={unmask(id || "")}
        />
      ),
    },
    {
      label: "Comments",
      key: "comments",
      children: (
        <Comments
          data={data}
          documents={documents}
          id={unmask(id || "")}
        />
      ),
    },
    ...(hasRole(["*"])
      ? [
          {
            label: "SPA Versions",
            key: "documentVersions",
            children: (
              <DocumentVerisoning
                data={data}
                reviewers={reviewers}
                documents={documents}
                watchers={watchers}
                fullEnvelopeDetails={fullEnvelopeDetails}
                setRefreshData={setRefreshData}
              />
            ),
          },
        ]
      : []),
    {
      label: "Logs",
      key: "logs",
      children: <Logs />,
    },
  ];

  return (
    <>
      <ContentWrapper
        isMobile={isMobile}
        loading={isLoading || isReviewersLoading || isContractLoading}
        customTitle={
          <Space style={{ alignItems: "center", lineHeight: 1 }}>
            <Icon
              onClick={() => {
                // const backUrl: any = getBackUrl(`/spa`);
                navigate(-1);
              }}
              icon="akar-icons:arrow-left"
              style={{
                fontSize: "1.8rem",
                cursor: "pointer",
                color: styles.primary,
                marginRight: styles.whitespace1,
              }}
            />
            <Typography.Title
              level={isMobile ? 4 : 3}
              style={{ margin: 0 }}
            >
              Project Details
            </Typography.Title>
          </Space>
        }
        actionItems={
          data?.status !== "draft" && (
            <Row
              style={{ width: "100%" }}
              justify={isMobile ? "start" : "end"}
            >
              <Col span={isMobile ? 24 : undefined}>
                <Dropdown
                  menu={{ items: dropdownItems }}
                  placement="bottomRight"
                  disabled={isPDFLoading}
                  trigger={isMobile ? ["click"] : ["hover"]}
                >
                  <Button
                    type="ghost"
                    loading={isPDFLoading}
                    block={isMobile}
                  >
                    Actions
                  </Button>
                </Dropdown>
              </Col>
            </Row>
          )
        }
      >
        <Tabs
          items={items}
          activeKey={query.get("activeTab") || ""}
          onChange={(activeKey) =>
            hasKeyString("watcher")
              ? navigate(`${window.location.pathname}?activeTab=${activeKey}&enable=return`)
              : !hasKeyString("review") && activeKey === "contractInformation"
                ? navigate(`${window.location.pathname}?activeTab=${activeKey}&enable=resend`)
                : navigate(`${window.location.pathname}?activeTab=${activeKey}`)
          }
        ></Tabs>
        <Formik
          initialValues={initialValue}
          validationSchema={validationSchema}
          onSubmit={submitForm}
        >
          {({ handleSubmit, resetForm, touched, errors, isValid, dirty, setFieldValue, setFieldTouched }) => (
            <Modal
              title="Send Document"
              maskClosable={false}
              open={sendModal}
              onOk={() => handleSubmit()}
              okButtonProps={{ disabled: !(isValid && dirty), loading }}
              onCancel={() => {
                resetForm();
                setSendModal(false);
              }}
              okText="Send"
              width={600}
              centered
            >
              <Form
                layout="vertical"
                onFinish={handleSubmit}
              >
                <Field name="email">
                  {({ field, meta }: any) => {
                    return (
                      <Form.Item
                        label="To"
                        validateStatus={meta.touched && meta.error ? "error" : ""}
                      >
                        <Select
                          mode="tags"
                          style={{ width: "100%" }}
                          size="middle"
                          {...field}
                          value={field.value}
                          onChange={(value) => setFieldValue(field.name, value)}
                          onBlur={() => {
                            setFieldTouched(field.name, true);
                          }}
                        >
                          {/* Options can be predefined here if needed */}
                        </Select>
                        {meta.touched && meta.error && (
                          <Typography.Text type="danger">{meta.error.toString()}</Typography.Text>
                        )}
                      </Form.Item>
                    );
                  }}
                </Field>
                <Field name="cc">
                  {({ field, meta }: any) => (
                    <Form.Item
                      label="Cc"
                      validateStatus={meta.touched && meta.error ? "error" : ""}
                    >
                      <Select
                        mode="tags"
                        style={{ width: "100%" }}
                        size="middle"
                        {...field}
                        value={field.value}
                        onChange={(value) => setFieldValue(field.name, value)}
                        onBlur={() => {
                          setFieldTouched(field.name, true);
                        }}
                      >
                        {/* Options can be predefined here if needed */}
                      </Select>
                      {meta.touched && meta.error && (
                        <Typography.Text type="danger">{meta.error.toString()}</Typography.Text>
                      )}
                    </Form.Item>
                  )}
                </Field>
                <Field name="bcc">
                  {({ field, meta }: any) => (
                    <Form.Item
                      label="Bcc"
                      validateStatus={meta.touched && meta.error ? "error" : ""}
                    >
                      <Select
                        mode="tags"
                        style={{ width: "100%" }}
                        size="middle"
                        {...field}
                        value={field.value}
                        onChange={(value) => setFieldValue(field.name, value)}
                        onBlur={() => {
                          setFieldTouched(field.name, true);
                        }}
                      >
                        {/* Options can be predefined here if needed */}
                      </Select>
                      {meta.touched && meta.error && (
                        <Typography.Text type="danger">{meta.error.toString()}</Typography.Text>
                      )}
                    </Form.Item>
                  )}
                </Field>
                <Field name="subject">
                  {({ field, meta }: any) => {
                    return (
                      <Form.Item
                        label="Email Subject"
                        validateStatus={meta.touched && meta.error ? "error" : ""}
                      >
                        <Input
                          maxLength={998}
                          showCount
                          {...field}
                        />
                        {meta.touched && meta.error && (
                          <Typography.Text type="danger">{meta.error.toString()}</Typography.Text>
                        )}
                      </Form.Item>
                    );
                  }}
                </Field>
                <Field name="message">
                  {({ field, meta }: any) => (
                    <Form.Item
                      label="Email Message"
                      validateStatus={meta.touched && meta.error ? "error" : ""}
                    >
                      <TextArea
                        rows={4}
                        maxLength={5000}
                        showCount
                        {...field}
                      />
                      {meta.touched && meta.error && (
                        <Typography.Text type="danger">{meta.error.toString()}</Typography.Text>
                      )}
                    </Form.Item>
                  )}
                </Field>
              </Form>
            </Modal>
          )}
        </Formik>
      </ContentWrapper>
      {isPreviewOpen && previewUrl && (
        <PDFViewer
          show={isPreviewOpen}
          title="View Document"
          subTitle=""
          setShow={(val) => {
            if (!val) {
              setIsPreviewOpen(false);
              if (previewUrl?.startsWith("blob:")) {
                URL.revokeObjectURL(previewUrl);
              }
              setPreviewUrl(null);
            }
          }}
          url={previewUrl}
        />
      )}
    </>
  );
};
